/**
 * Color palette per pitch class.
 * Sharps/flats take the color of their base letter.
 */

const PITCH_CLASS_COLORS: number[] = [
  0xff6b6b, // C  - red
  0xff6b6b, // C# - red
  0xff9f43, // D  - orange
  0xff9f43, // D# - orange
  0xfeca57, // E  - yellow
  0x1dd1a1, // F  - green
  0x1dd1a1, // F# - green
  0x48dbfb, // G  - cyan
  0x48dbfb, // G# - cyan
  0x5f8cff, // A  - blue
  0x5f8cff, // A# - blue
  0xa55eea, // B  - violet
];

const PITCH_CLASS_NAMES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];

/** Get the color for a pitch class (0-11) or MIDI note number. */
export function pitchClassColor(pcOrMidi: number): number {
  return PITCH_CLASS_COLORS[pcOrMidi % 12];
}

/** Get pitch class name (sharp notation) from pitch class (0-11) or MIDI note. */
export function pitchClassName(pcOrMidi: number): string {
  return PITCH_CLASS_NAMES[pcOrMidi % 12];
}

/** Get note name with octave from a MIDI note number (e.g. 60 → "C4"). */
export function pitchNameWithOctave(midi: number): string {
  const octave = Math.floor(midi / 12) - 1;
  return PITCH_CLASS_NAMES[midi % 12] + octave;
}

const CHORD_DEGREES = ['1', 'b2', '2', '3', '3', '4', 'b5', '5', 'b6', '6', '7', '7'];

/** Get chord degree relative to a root pitch class (e.g. root=9 (A), midi=64 (E4) → "5"). */
export function chordDegree(rootPc: number, midi: number): string {
  return CHORD_DEGREES[((midi % 12) - rootPc + 12) % 12];
}

// ── Harmony-aware degree formatting ─────────────────────────────

const TONE_TO_LABEL: Record<string, string> = {
  'root':  '1',
  'b9':    '♭9',
  '9th':   '9',
  'b3rd':  '♭3',
  '3rd':   '3',
  '11th':  '4',
  '#11':   '♯11',
  '4th':   '4',
  '5th':   '5',
  'b13':   '♭13',
  '6th':   '6',
  '13th':  '6',
  'b7th':  '♭7',
  '7th':   '7',
  '#9':    '♯9',
};

/** Format a harmony tone value (from the analyzer API) into a readable degree label. */
export function formatDegree(tone: string): string {
  return TONE_TO_LABEL[tone] ?? tone;
}

const DEGREE_ORDER = ['1', '♭9', '9', '♭3', '♯9', '3', '4', '♯11', '5', '♭13', '6', '♭7', '7'];

/** Sort degree labels by musical pitch order (low to high). */
export function sortDegrees(degrees: string[]): string[] {
  return [...degrees].sort((a, b) => {
    const ia = DEGREE_ORDER.indexOf(a);
    const ib = DEGREE_ORDER.indexOf(b);
    return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
  });
}
